import {
  takeLatest,
  call,
  put,
  select,
} from 'redux-saga/effects';
import axios from '../axiosConfig';
import {
  getInsuranceQuotesRequest,
  getInsuranceQuotesSuccess,
  getInsuranceQuotesFailure,
  selectInsuranceOption,
} from '../../slices/insurance';
import { transformTripDataForInsurance, validateInsuranceRequest } from '../../../helpers/insuranceDataTransform';

/**
 * Get insurance quotes worker saga
 * @param {Object} action - Redux action
 * @param {Object} action.payload - Action payload
 * @param {string} action.payload.tripId - Trip ID
 */
function* getInsuranceQuotesWorker({ payload: { tripId } }) {
  try {
    // Get trip and passengers data from Redux state
    const trip = yield select((state) => state.trip.selectedTrips[tripId]);
    const passengers = yield select((state) => state.trip.passengers);

    if (!trip) {
      throw new Error('Trip not found');
    }

    if (!passengers || Object.keys(passengers).length === 0) {
      throw new Error('Passenger information is required for insurance quotes');
    }

    // Transform trip data for insurance API
    const requestData = transformTripDataForInsurance(trip, passengers);

    // Validate request data
    const validation = validateInsuranceRequest(requestData);
    if (!validation.isValid) {
      throw new Error(`Validation failed: ${validation.errors.join(', ')}`);
    }

    // Make API call
    const config = {
      method: 'POST',
      url: '/api/insurance/getquotes',
      data: requestData,
      headers: {
        'Content-Type': 'application/json',
      },
    };

    const { data } = yield call(axios, config);

    // Validate response
    if (!data || !data.success) {
      throw new Error(data?.errorMessage || 'Failed to retrieve insurance quotes');
    }

    if (!data.quotes || !Array.isArray(data.quotes)) {
      throw new Error('Invalid insurance quotes response format');
    }

    // Dispatch success action
    yield put(getInsuranceQuotesSuccess({
      tripId,
      quotes: data.quotes,
      retrievedAt: data.retrievedAt || new Date().toISOString(),
    }));
  } catch (error) {
    console.error('Insurance quotes error:', error);

    let errorMessage = 'Unable to retrieve insurance quotes at this time';

    if (error.response) {
      // API error response
      const { status } = error.response;
      if (status >= 500) {
        errorMessage = 'Insurance service is temporarily unavailable. Please try again later.';
      } else if (status === 404) {
        errorMessage = 'Insurance service not available for this trip.';
      } else {
        errorMessage = error.response.data?.errorMessage
                      || error.response.data?.message
                      || 'Unable to retrieve insurance quotes at this time.';
      }
    } else if (error.message) {
      // Custom error message - make it more user-friendly
      if (error.message.includes('ArchRoam')) {
        errorMessage = 'Insurance quotes are temporarily unavailable. Please try again later.';
      } else if (error.message.includes('Validation failed')) {
        errorMessage = 'Please ensure all traveler information is complete and try again.';
      } else {
        errorMessage = error.message;
      }
    }

    yield put(getInsuranceQuotesFailure({
      tripId,
      error: errorMessage,
    }));
  }
}

/**
 * Select insurance option worker saga
 * @param {Object} action - Redux action
 * @param {Object} action.payload - Action payload
 * @param {string} action.payload.tripId - Trip ID
 * @param {string} action.payload.quoteId - Selected quote ID
 */
function* selectInsuranceOptionWorker({ payload: { tripId, quoteId } }) {
  try {
    // Get quotes from state
    const quotes = yield select((state) => state.insurance.insuranceQuotes[tripId]);

    if (!quotes || !Array.isArray(quotes)) {
      throw new Error('No insurance quotes available');
    }

    // Find the selected quote
    const selectedQuote = quotes.find((quote) => quote.id === quoteId || quote.planId === quoteId);

    if (!selectedQuote) {
      throw new Error('Selected insurance quote not found');
    }

    // Dispatch selection action
    yield put(selectInsuranceOption({
      tripId,
      quoteId,
      quote: selectedQuote,
    }));
  } catch (error) {
    console.error('Insurance selection error:', error);
    // Note: We don't dispatch failure here as selection is local state
    // The component can handle the error locally
  }
}

/**
 * Insurance quotes watcher saga
 */
// eslint-disable-next-line no-unused-vars
function* insuranceQuotesWatcher() {
  yield takeLatest(getInsuranceQuotesRequest.toString(), getInsuranceQuotesWorker);
}

/**
 * Insurance selection watcher saga
 */
// eslint-disable-next-line no-unused-vars
function* insuranceSelectionWatcher() {
  yield takeLatest(selectInsuranceOption.toString(), selectInsuranceOptionWorker);
}

/**
 * Main insurance watcher saga
 */
export default function* insuranceWatcher() {
  yield takeLatest(getInsuranceQuotesRequest.toString(), getInsuranceQuotesWorker);
  yield takeLatest(selectInsuranceOption.toString(), selectInsuranceOptionWorker);
}
